import TextField from "@/components/common/text-field";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import {
  Button,
  Heading,
  Icon,
  Radiobutton,
} from "@elaniintc/goes-design-system";
import type { Dispatch, SetStateAction } from "react";
import SwitchField from "@/workflow/components/configurations/form/components/SwitchField";
import InfoMessage from "@/workflow/components/configurations/form/components/InfoMessage";
import { DataCenterField } from "../components/DataCenterField";

interface FieldOptionsSectionProps {
  idPrefix: string;
  hasDropdown: boolean;
  setHasDropdown: Dispatch<SetStateAction<boolean>>;
  buttonPosition: "left" | "right";
  setButtonPosition: Dispatch<SetStateAction<"left" | "right">>;
  valuesSource: "database" | "manual";
  setValuesSource: Dispatch<SetStateAction<"database" | "manual">>;
  localListNewItem: string;
  setLocalListNewItem: Dispatch<SetStateAction<string>>;
  handleAddLocalItem: () => void;
  localListItems: string[];
  handleRemoveLocalItem: (index: number) => void;
  valuesTableName: string;
  valuesColumnName: string;
  setDataCenterValue: (tableName: string, columnName: string) => void;
  expandSelectedRecord: boolean;
  setExpandSelectedRecord: Dispatch<SetStateAction<boolean>>;
}

export const FieldOptionsSection = ({
  idPrefix,
  hasDropdown,
  setHasDropdown,
  buttonPosition,
  setButtonPosition,
  valuesSource,
  setValuesSource,
  localListNewItem,
  setLocalListNewItem,
  handleAddLocalItem,
  localListItems,
  handleRemoveLocalItem,
  valuesTableName,
  valuesColumnName,
  setDataCenterValue,
  expandSelectedRecord,
  setExpandSelectedRecord,
}: FieldOptionsSectionProps) => {
  return (
    <div className="flex flex-col gap-4">
      <Heading variant="h6" className="mb-0!" color="loud">
        Opciones
      </Heading>

      <SwitchField
        id={`${idPrefix}-dropdown`}
        label="¿Agregar botón dropdown al campo?"
        required
        checked={hasDropdown}
        onChange={setHasDropdown}
        isTrue="Sí"
        isFalse="No"
      />

      {hasDropdown && (
        <>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={`${idPrefix}-button-position`}
              className="font-semibold text-body-loud text-sm leading-2xl"
            >
              Posición del botón <span className="text-sm text-danger">*</span>
            </label>
            <DropdownList
              id={`${idPrefix}-button-position`}
              options={[
                { value: "left", label: "Izquierda" },
                { value: "right", label: "Derecha" },
              ]}
              placeholder="Seleccionar"
              selectedValue={{
                value: buttonPosition,
                label: buttonPosition === "left" ? "Izquierda" : "Derecha",
              }}
              toggleSelection={(item) => {
                if (item.value === "left" || item.value === "right") {
                  setButtonPosition(item.value);
                }
              }}
              widthClassName="w-full"
              scrollClassName="h-auto"
            />
          </div>

          <div className="flex flex-col gap-1">
            <div className="font-semibold text-body-loud text-sm leading-2xl">
              ¿De donde provienen los valores?{" "}
              <span className="text-sm text-danger">*</span>
            </div>
            <div className="flex flex-col gap-2 mt-1">
              <div className="flex items-center gap-3">
                <Radiobutton
                  id={`${idPrefix}-source-database`}
                  name={`${idPrefix}-source`}
                  value="database"
                  checked={valuesSource === "database"}
                  onChange={() => setValuesSource("database")}
                />
                <label
                  htmlFor={`${idPrefix}-source-database`}
                  className="text-body text-sm"
                >
                  Desde centro de datos
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Radiobutton
                  id={`${idPrefix}-source-manual`}
                  name={`${idPrefix}-source`}
                  value="manual"
                  checked={valuesSource === "manual"}
                  onChange={() => setValuesSource("manual")}
                />
                <label
                  htmlFor={`${idPrefix}-source-manual`}
                  className="text-body text-sm"
                >
                  Locales
                </label>
              </div>
            </div>
          </div>

          {valuesSource === "manual" && (
            <div className="flex flex-col gap-2">
              <div className="flex items-end group">
                <div className="w-full">
                  <TextField
                    placeholder="Añade un nuevo item"
                    className="rounded-tr-none rounded-br-none border-r-0"
                    label="Lista local"
                    required
                    value={localListNewItem}
                    onChange={(e) => setLocalListNewItem(e.target.value)}
                  />
                </div>
                <Button
                  className="h-11 rounded-bl-none rounded-tl-none border-l-0 group-hover:border-neutral-loud group-focus-within:border-contextual-action! group-hover:bg-neutral-object-accent-silent group-focus-within:bg-neutral-object-accent-quiet!"
                  variant="quiet"
                  type="button"
                  onClick={handleAddLocalItem}
                  aria-label="Agregar item"
                >
                  <Icon name="plus" type="far" />
                </Button>
              </div>

              <InfoMessage>
                El primer valor de la lista será el pre-seleccionado por default
              </InfoMessage>

              {localListItems.length > 0 && (
                <div className="w-full rounded-lg border border-neutral bg-neutral-object shadow-sm p-1">
                  <div className="flex flex-col">
                    {localListItems.map((item, index) => (
                      <div
                        key={`${item}-${index}`}
                        className="px-3 py-2 flex items-center gap-3"
                      >
                        <div className="flex-1 text-body text-sm truncate">
                          {item}
                        </div>
                        <Button
                          variant="quiet"
                          size="icon"
                          type="button"
                          aria-label="Eliminar"
                          onClick={() => handleRemoveLocalItem(index)}
                          className="w-8 h-8"
                        >
                          <Icon type="far" name="times" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {valuesSource === "database" && (
            <DataCenterField
              required
              fieldKey="valueDatacenter"
              selectedTable={valuesTableName}
              selectedColumn={valuesColumnName}
              onChange={setDataCenterValue}
            />
          )}
          {valuesSource === "database" && (
            <SwitchField
              id={`${idPrefix}-expand-selected`}
              label="Expandir registro seleccionado"
              required
              checked={expandSelectedRecord}
              onChange={setExpandSelectedRecord}
              isTrue="Sí, expandir"
              isFalse="No, generar una sola variable (array)"
            />
          )}
        </>
      )}
    </div>
  );
};
